import { Select as MantineSelect, type SelectProps } from "@mantine/core";
import { useCallback, useMemo } from "react";
import {
  normalizeSelectData,
  normalizeSelectValue,
} from "src/utils/mantineComboboxData";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

interface Props<T extends FieldValues> extends SelectProps {
  name: Path<T>;
  control: Control<T>;
  onChange?: (value: string) => void | Promise<void>;
  required?: boolean;
}

function Select<T extends FieldValues>({
  name,
  control,
  onChange,
  required,
  data,
  ...props
}: Props<T>) {
  const {
    field: { onChange: defaultOnChange, ...field },
    fieldState: { invalid },
  } = useController({
    name,
    control,
    rules: { required },
  });

  const handleChange = useCallback(
    async (value: string | null) => {
      defaultOnChange(value ?? "");
      if (onChange && value) await onChange(value);
    },
    [defaultOnChange, onChange],
  );

  const normalizedData = useMemo(
    () => (data ? normalizeSelectData(data) : data),
    [data],
  );

  const selectValue = normalizeSelectValue(field.value);

  return (
    <MantineSelect
      {...field}
      {...props}
      data={normalizedData}
      value={selectValue}
      onChange={handleChange}
      error={invalid}
    />
  );
}

export default Select;
